import {
  FormControl,
  type FormControlChangeEvent,
} from '@jouzen/ecom-components/v3/FormControl';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';

import {
  findProductDataBySku,
  findSku,
  findVariant,
  isVariantInStock,
} from '@/app/utils';
import { Product } from '@/queries/types/graphql';
import { OrderLineItem } from '@/types/Order';

import Image from '../../Image';
import { FinishOption } from '../types';
import useProductOptionData from '../useProductOptionsData';

type Trait = string | undefined;

export interface MultiRingItem extends OrderLineItem {
  id: string | number;
}

export interface RingItemProps {
  readonly item: MultiRingItem;
  readonly products: Product[];
  readonly onChange: (updatedItem: MultiRingItem) => void;
}

const RingItem = ({ item, products, onChange }: RingItemProps): JSX.Element => {
  const t = useTranslations();

  const { finish: initFinish, size: initSize } = findProductDataBySku(
    products,
    item.sku,
  );
  const [finish, setFinish] = useState<Trait>(initFinish);
  const [size, setSize] = useState<string>(initSize ?? '');

  const { finishOptions, sizeOptions } = useProductOptionData(products, {
    // Scope the sizes to the selected finish so each size's stock state is
    // that finish's, not a cross-finish union.
    selectedSku: finish ? findSku(products, finish) : undefined,
  });

  const handleChange = useCallback(
    (newFinish: Trait, newSize: Trait) => {
      const newSku = newFinish
        ? findSku(products, newFinish, newSize)
        : item.originalSku;

      onChange({ ...item, sku: newSku ?? item.originalSku });
    },
    [item, onChange, products],
  );

  const handleFinishChange = (e: FormControlChangeEvent) => {
    const newFinish = e.target.value;
    // Don't carry the size over when it's out of stock in the new finish —
    // reset to "size pending" so an out-of-stock SKU is never selected.
    const variant = newFinish
      ? findVariant(products, newFinish, size)
      : undefined;
    const newSize = isVariantInStock(variant) ? size : '';

    handleChange(newFinish, newSize);
    setFinish(newFinish);
    setSize(newSize);
  };

  const handleSizeChange = (e: FormControlChangeEvent) => {
    const newSize = e.target.value;
    // The Select can't disable individual options, so out-of-stock sizes stay
    // selectable (labeled "- Out of stock") — reject such a pick and reset to
    // "size pending" so an out-of-stock SKU is never selected.
    const variant = finish ? findVariant(products, finish, newSize) : undefined;
    const nextSize = isVariantInStock(variant) ? newSize : '';

    handleChange(finish, nextSize);
    setSize(nextSize);
  };

  const img = useMemo(() => {
    const { imgSrc, label }: FinishOption | undefined =
      finishOptions.find((opt) => opt.value === finish) ??
      finishOptions[0] ??
      {};

    return {
      src: imgSrc,
      alt: label,
    };
  }, [finish, finishOptions]);

  const styles = {
    select: 'min-w-32 shrink-0 pb-2',
  };

  return (
    <div
      data-testid="product-select-ring-item"
      className="flex w-full items-center lg:gap-2"
    >
      {img.src && (
        <div
          className={cx('shrink-0 hidden sm:block', { 'opacity-40': !finish })}
        >
          <Image width="96" height="96" src={img.src} alt={img.alt} />
        </div>
      )}

      <div className="grow gap-4 lg:flex">
        <FormControl
          hideError
          name="finish"
          control="Select"
          className={cx(styles.select, 'grow')}
          options={finishOptions}
          label={t('step_finish_title')}
          onChange={handleFinishChange}
          value={finish!}
        />

        <FormControl
          hideError
          name="size"
          control="Select"
          className={styles.select}
          // The Select can't disable individual options (it only reads
          // value/label from each one), so out-of-stock sizes are labeled
          // instead, and handleSizeChange rejects picking them. Switch to a
          // per-option `disabled` if ecom-components gains support.
          options={sizeOptions.map(({ label, value, inStock }) => ({
            value,
            label: inStock ? label : `${label} - ${t('out_of_stock_tag')}`,
          }))}
          label={t('step_size_title')}
          onChange={handleSizeChange}
          value={size}
        />
      </div>
    </div>
  );
};

export default RingItem;
